import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PostTag } from '@/core/services/interfaces/PostInterfaces';
import { BdsIcon, BdsChipClickable, BdsTooltip } from '../../../../core/constants/webComponents';
import { CARD_POST_EVENTS, CardPostEventName } from '@/features/publication-wall/constants/events';
import selectorService from '@/core/services/SelectorService';
import './TagAddModal.scss';

interface TagAddModalProps {
  tags: PostTag[];
  existingTags?: string[];
  postId?: string;
  eventName?: CardPostEventName;
  onClose: () => void;
  onTagAdded: (tag: string) => void;
  onTagRemoved?: (tag: string) => void;
}

export const TagAddModal: React.FC<TagAddModalProps> = ({
  tags: initialTags,
  existingTags = [],
  postId,
  eventName = CARD_POST_EVENTS.TAG_MODAL_CLICK,
  onClose,
  onTagAdded,
  onTagRemoved,
}) => {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const confirmDialogRef = useRef<HTMLDialogElement>(null);
  const removeDialogRef = useRef<HTMLDialogElement>(null);
  const autocompleteRef = useRef<HTMLBdsAutocompleteElement>(null);
  const chipRefs = useRef<(HTMLElement | null)[]>([]);
  const chipTextRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const pendingActionRef = useRef(false);
  const onTagRemovedRef = useRef(onTagRemoved);
  useEffect(() => { onTagRemovedRef.current = onTagRemoved; });
  const [inputValue, setInputValue] = useState('');
  const [overflowingTags, setOverflowingTags] = useState<Set<number>>(new Set());
  const [pendingTag, setPendingTag] = useState<string | null>(null);
  const [pendingRemoveTag, setPendingRemoveTag] = useState<string | null>(null);
  const [localTags, setLocalTags] = useState<PostTag[]>(initialTags);
  const [fetchedTags, setFetchedTags] = useState<string[] | null>(null);

  // Host-provided list can be stale; the backend list is the source of truth when available.
  useEffect(() => {
    let cancelled = false;
    selectorService
      .getAllTagsPaginated()
      .then((result) => { if (!cancelled) setFetchedTags(result.tags); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const availableTags = fetchedTags ?? existingTags;

  const normalizeTag = useCallback((value: string) => value.trim().toLocaleLowerCase(), []);

  const normalizedExistingTags = useMemo(
    () => availableTags.map((tag) => normalizeTag(tag)),
    [availableTags, normalizeTag],
  );

  const knownExistingTags = useMemo(() => {
    const seen = new Set<string>();
    const result: string[] = [];

    for (const tag of availableTags) {
      const trimmed = tag.trim();
      if (!trimmed) continue;
      const normalized = normalizeTag(trimmed);
      if (seen.has(normalized)) continue;
      seen.add(normalized);
      result.push(trimmed);
    }

    return result;
  }, [availableTags, normalizeTag]);

  const localTagSet = useMemo(
    () => new Set(localTags.map((tag) => normalizeTag(tag.label))),
    [localTags, normalizeTag],
  );

  const suggestedTags = useMemo(
    () => knownExistingTags.filter((tag) => !localTagSet.has(normalizeTag(tag))),
    [knownExistingTags, localTagSet, normalizeTag],
  );

  useEffect(() => {
    setLocalTags(initialTags);
  }, [initialTags]);

  useEffect(() => {
    const frames = [
      requestAnimationFrame(() => {
        const next = new Set<number>();
        chipTextRefs.current.forEach((el, index) => {
          if (el && el.scrollWidth > el.offsetWidth) next.add(index);
        });
        setOverflowingTags(next);
      }),
      requestAnimationFrame(() => {
        const next = new Set<number>();
        chipTextRefs.current.forEach((el, index) => {
          if (el && el.scrollWidth > el.offsetWidth) next.add(index);
        });
        setOverflowingTags(next);
      }),
    ];
    return () => frames.forEach(cancelAnimationFrame);
  }, [localTags]);

  const dispatchTagModalClick = useCallback(
    (
      action: 'close' | 'existing-tag' | 'remove-tag' | 'cancel' | 'add' | 'confirm-create' | 'confirm-cancel' | 'confirm-remove' | 'cancel-remove',
      value?: string,
      index?: number,
    ) => {
      document.dispatchEvent(
        new CustomEvent(eventName, {
          detail: {
            postId,
            action,
            value,
            index,
          },
          bubbles: true,
          composed: true,
        }),
      );
    },
    [eventName, postId],
  );

  const handleCancelConfirm = useCallback(() => {
    dispatchTagModalClick('confirm-cancel', pendingTag ?? undefined);
    setPendingTag(null);
  }, [dispatchTagModalClick, pendingTag]);

  const handleCancelRemove = useCallback(() => {
    dispatchTagModalClick('cancel-remove', pendingRemoveTag ?? undefined);
    setPendingRemoveTag(null);
  }, [dispatchTagModalClick, pendingRemoveTag]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (!dialog.open) {
      try {
        dialog.showModal();
      } catch {
        dialog.setAttribute('open', '');
      }
    }

    const handleClick = (e: MouseEvent) => {
      if (e.target === dialog) { dispatchTagModalClick('close', 'overlay'); onClose(); }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { dispatchTagModalClick('close', 'escape'); onClose(); }
    };
    dialog.addEventListener('click', handleClick);
    dialog.addEventListener('keydown', handleKeyDown);
    return () => {
      dialog.removeEventListener('click', handleClick);
      dialog.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose, dispatchTagModalClick]);

  useEffect(() => {
    const autocomplete = autocompleteRef.current;
    if (!autocomplete) return;

    const handleAutocompleteChange = (event: Event) => {
      const customEvent = event as CustomEvent;
      const detail = customEvent.detail as { value?: string } | string | undefined;

      if (typeof detail === 'string') {
        if (detail) setInputValue(detail);
        return;
      }

      if (detail && typeof detail.value === 'string') {
        if (detail.value) setInputValue(detail.value);
        return;
      }

      const target = event.target as { value?: unknown } | null;
      if (target && typeof target.value === 'string' && target.value) {
        setInputValue(target.value);
      }
    };

    autocomplete.addEventListener('bdsInput', handleAutocompleteChange);
    autocomplete.addEventListener('bdsChange', handleAutocompleteChange);
    autocomplete.addEventListener('input', handleAutocompleteChange);

    return () => {
      autocomplete.removeEventListener('bdsInput', handleAutocompleteChange);
      autocomplete.removeEventListener('bdsChange', handleAutocompleteChange);
      autocomplete.removeEventListener('input', handleAutocompleteChange);
    };
  }, []);

  useEffect(() => {
    const confirm = confirmDialogRef.current;
    if (!confirm) return;
    if (!confirm.open) {
      try {
        confirm.showModal();
      } catch {
        confirm.setAttribute('open', '');
      }
    }

    const handleClick = (e: MouseEvent) => { if (e.target === confirm) handleCancelConfirm(); };
    const handleKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') handleCancelConfirm(); };
    confirm.addEventListener('click', handleClick);
    confirm.addEventListener('keydown', handleKeyDown);
    return () => {
      confirm.removeEventListener('click', handleClick);
      confirm.removeEventListener('keydown', handleKeyDown);
    };
  }, [pendingTag, handleCancelConfirm]);

  useEffect(() => {
    const removeDialog = removeDialogRef.current;
    if (!removeDialog) return;
    if (!removeDialog.open) {
      try {
        removeDialog.showModal();
      } catch {
        removeDialog.setAttribute('open', '');
      }
    }

    const handleClick = (e: MouseEvent) => { if (e.target === removeDialog) handleCancelRemove(); };
    const handleKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') handleCancelRemove(); };
    removeDialog.addEventListener('click', handleClick);
    removeDialog.addEventListener('keydown', handleKeyDown);
    return () => {
      removeDialog.removeEventListener('click', handleClick);
      removeDialog.removeEventListener('keydown', handleKeyDown);
    };
  }, [pendingRemoveTag, handleCancelRemove]);

  const handleAddClick = () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;

    const normalizedInput = normalizeTag(trimmed);
    if (localTagSet.has(normalizedInput)) {
      setInputValue('');
      return;
    }

    dispatchTagModalClick('add', trimmed);

    if (normalizedExistingTags.includes(normalizedInput)) {
      const match = knownExistingTags.find((tag) => normalizeTag(tag) === normalizedInput) ?? trimmed;
      onTagAdded(match);
      setLocalTags((prev) => [...prev, { label: match }]);
      setInputValue('');
      onClose();
      return;
    }

    pendingActionRef.current = false;
    setPendingTag(trimmed);
  };

  const handleConfirmCreate = () => {
    if (!pendingTag || pendingActionRef.current) return;
    pendingActionRef.current = true;
    dispatchTagModalClick('confirm-create', pendingTag);
    selectorService.addTags([...availableTags, pendingTag]).catch((err: unknown) => {
      console.error('[TagAddModal] Failed to persist new tag:', err);
    });
    onTagAdded(pendingTag);
    setLocalTags((prev) => [...prev, { label: pendingTag }]);
    setInputValue('');
    setPendingTag(null);
    onClose();
  };

  const handleTagChipClick = useCallback((tagLabel: string, index: number) => {
    pendingActionRef.current = false;
    if (onTagRemovedRef.current) {
      dispatchTagModalClick('remove-tag', tagLabel, index);
      setPendingRemoveTag(tagLabel);
    } else {
      dispatchTagModalClick('existing-tag', tagLabel, index);
    }
  }, [dispatchTagModalClick]);

  const handleConfirmRemove = () => {
    if (!pendingRemoveTag || pendingActionRef.current) return;
    pendingActionRef.current = true;
    dispatchTagModalClick('confirm-remove', pendingRemoveTag);
    onTagRemovedRef.current?.(pendingRemoveTag);
    setLocalTags((prev) => prev.filter((t) => t.label !== pendingRemoveTag));
    setPendingRemoveTag(null);
  };

  useEffect(() => {
    const cleanups: (() => void)[] = [];

    localTags.forEach((tag, index) => {
      const el = chipRefs.current[index];
      if (!el) return;

      if (onTagRemoved) {
        const handler = () => handleTagChipClick(tag.label, index);
        el.addEventListener('chipClickableClose', handler);
        cleanups.push(() => el.removeEventListener('chipClickableClose', handler));
      } else {
        const handler = () => dispatchTagModalClick('existing-tag', tag.label, index);
        el.addEventListener('chipClickableClick', handler);
        cleanups.push(() => el.removeEventListener('chipClickableClick', handler));
      }
    });

    return () => cleanups.forEach((fn) => fn());
  }, [localTags, onTagRemoved, handleTagChipClick, dispatchTagModalClick]);

  return (
    <>
      <dialog
        ref={dialogRef}
        className="tag-add-modal__overlay"
        onClose={onClose}
        aria-label={t('cardPosts.addTag')}
      >
        <div className="tag-add-modal">
          {/* Close button */}
          <button
            className="tag-add-modal__close"
            onClick={() => { dispatchTagModalClick('close', 'button'); onClose(); }}
            aria-label={t('cardPosts.close')}
            title={t('cardPosts.close')}
          >
            <BdsIcon theme="outline" name="close" size="small" />
          </button>

          {/* Chips display — clique remove a tag */}
          <div className="tag-add-modal__chips">
            {localTags.length === 0 ? (
              <span className="tag-add-modal__empty">{t('cardPosts.tagModalEmpty')}</span>
            ) : (
              localTags.map((tag, index) => (
                <span key={tag.label} className="tag-add-modal__chip-wrapper">
                  <BdsTooltip
                    position="top-center"
                    tooltip-text={tag.label}
                    disabled={!overflowingTags.has(index)}
                  >
                    <span className="tag-add-modal__chip-tooltip-trigger">
                      <BdsChipClickable
                        ref={(el: HTMLElement | null) => { chipRefs.current[index] = el; }}
                        color="outline"
                        close={onTagRemoved ? 'true' : 'false'}
                        aria-label={onTagRemoved ? t('cardPosts.tagModalRemove', { tag: tag.label }) : tag.label}
                      >
                        <span className="tag-add-modal__chip-text" ref={(el) => { chipTextRefs.current[index] = el; }}>{tag.label}</span>
                      </BdsChipClickable>
                    </span>
                  </BdsTooltip>
                </span>
              ))
            )}
          </div>

          {/* Input */}
          <div className="tag-add-modal__input-wrapper">
            <bds-autocomplete
              ref={autocompleteRef}
              class="tag-add-modal__autocomplete"
              data-testid="tag-modal-autocomplete"
              placeholder={t('cardPosts.tagModalInputPlaceholder')}
              title={t('cardPosts.tagModalInputPlaceholder')}
              value={inputValue}
              selection-type="single"
            >
              {suggestedTags.map((tag) => (
                <bds-select-option
                  key={tag}
                  value={tag}
                  onClick={() => setInputValue(tag)}
                >
                  {tag}
                </bds-select-option>
              ))}
            </bds-autocomplete>
          </div>

          {/* Footer buttons */}
          <div className="tag-add-modal__footer">
            <button
              className="tag-add-modal__btn tag-add-modal__btn--secondary"
              onClick={() => { dispatchTagModalClick('cancel'); onClose(); }}
              title={t('cardPosts.tagModalCancel')}
            >
              {t('cardPosts.tagModalCancel')}
            </button>
            <button
              className="tag-add-modal__btn tag-add-modal__btn--primary"
              onClick={handleAddClick}
              disabled={!inputValue.trim()}
              title={t('cardPosts.tagModalAdd')}
            >
              {t('cardPosts.tagModalAdd')}
            </button>
          </div>
        </div>
      </dialog>

      {pendingTag && (
        <dialog
          ref={confirmDialogRef}
          className="tag-confirm__overlay"
          onClose={() => { if (!pendingActionRef.current) handleCancelConfirm(); }}
          aria-labelledby="tag-confirm-title"
        >
          <div className="tag-confirm">
            <div className="tag-confirm__header">
              <BdsIcon theme="outline" name="warning" size="medium" />
              <span id="tag-confirm-title" className="tag-confirm__title">
                {t('cardPosts.tagModalConfirmTitle')}
              </span>
            </div>
            <div className="tag-confirm__body">
              <p className="tag-confirm__text">
                {t('cardPosts.tagModalConfirmMessage', { tag: pendingTag ?? '' })}
              </p>
              <div className="tag-confirm__buttons">
                <button className="tag-confirm__btn tag-confirm__btn--secondary" onClick={handleCancelConfirm} title={t('cardPosts.tagModalCancelCreate')}>
                  {t('cardPosts.tagModalCancelCreate')}
                </button>
                <button className="tag-confirm__btn tag-confirm__btn--primary" onClick={handleConfirmCreate} title={t('cardPosts.tagModalConfirmCreate')}>
                  {t('cardPosts.tagModalConfirmCreate')}
                </button>
              </div>
            </div>
          </div>
        </dialog>
      )}

      {pendingRemoveTag && (
        <dialog
          ref={removeDialogRef}
          className="tag-confirm__overlay"
          onClose={() => { if (!pendingActionRef.current) handleCancelRemove(); }}
          aria-labelledby="tag-remove-title"
        >
          <div className="tag-confirm">
            <div className="tag-confirm__header">
              <BdsIcon theme="outline" name="warning" size="medium" />
              <span id="tag-remove-title" className="tag-confirm__title">
                {t('cardPosts.tagModalRemoveConfirmTitle')}
              </span>
            </div>
            <div className="tag-confirm__body">
              <p className="tag-confirm__text">
                {t('cardPosts.tagModalRemoveConfirmMessage', { tag: pendingRemoveTag })}
              </p>
              <div className="tag-confirm__buttons">
                <button className="tag-confirm__btn tag-confirm__btn--secondary" onClick={handleCancelRemove} title={t('cardPosts.tagModalCancelRemove')}>
                  {t('cardPosts.tagModalCancelRemove')}
                </button>
                <button className="tag-confirm__btn tag-confirm__btn--primary" onClick={handleConfirmRemove} title={t('cardPosts.tagModalConfirmRemove')}>
                  {t('cardPosts.tagModalConfirmRemove')}
                </button>
              </div>
            </div>
          </div>
        </dialog>
      )}
    </>
  );
};